import React, { useCallback, useContext, useEffect } from "react";
import { Link, Outlet } from "react-router-dom";
import { integrationService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { App, Integration } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useNumberId } from "/app/src/hooks/useNumberId";
import { Button, Chip } from "@heroui/react";
import ImportTemplate from "./importTemplate";
import NavigationMenuContext from "/app/src/contexts/NavigationMenuContext";
/**
 * Renders the controls component for managing integrations and toggling the display of additional content.
 *
 * @param {Object} param0 - The component props.
 * @param {App} param0.app - The application object.
 * @param {string} param0.name - The identifier name for the controls element.
 * @param {React.ReactNode} [param0.children] - Optional React children nodes to be rendered.
 * @param {boolean} param0.toggled - Boolean value indicating if the toggle is active.
 * @param {function} param0.setToggled - Function to update the toggled state.
 * @param {boolean} [param0.canImport=false] - Boolean indicating if import functionality is enabled.
 * @returns {JSX.Element} The rendered Controls component.
 */
export default function Controls({
  app,
  name,
  children,
  toggled,
  setToggled,
  canImport = false,
}: {
  app: App;
  name: string;
  children?: React.ReactNode;
  toggled: boolean;
  setToggled: (toggled: boolean) => void;
  canImport?: boolean;
}) {
  const { t } = useTranslation();
  const navigationMenu = useContext(NavigationMenuContext);
  const { integrationId } = useNumberId();

  //get single integration
  const { data: integration } = useQuery({
    queryKey: ["integration", Number(integrationId)],
    queryFn: () => {
      return integrationService.getSingle(integrationId);
    },
    enabled: Boolean(integrationId),
    initialData: { integration: {} },
    select: (data: { integration: Integration }) => {
      return data.integration;
    },
  });

  useEffect(() => {
    if (integrationId) {
      if (toggled) {
        //toggle the new integration form to hidden
        setToggled(false);
      }
    }
  }, [setToggled, toggled, integrationId]);

  const toggleOn = useCallback(() => {
    setToggled(true);
  }, [setToggled]);

  const toggleOff = useCallback(() => {
    setToggled(false);
  }, [setToggled]);

  return (
    <>
      <div className="wrapper">
        <div
          id={name}
          className={`app controls mt-0 ${navigationMenu === "1" ? " -mx-[16px] mb-[25px]" : "-mx-[25px] mb-[25px]"}`}
        >
          <div className="icon"> </div>

          {integration?.id ? (
            <div className="title flex items-center space-x-2">
              <Link to={`/apps/${app.id}`}>{app.name}</Link>
              {app.name === "Order Processor" && (
                <Chip size="sm" color="secondary" variant="shadow">
                  {t("translation:beta")}
                </Chip>
              )}
              <span className="separator">{">"}</span>
              <h1>{integration?.name}</h1>
            </div>
          ) : (
            <div className="title flex items-center space-x-2">
              <h1>{app.name}</h1>
              {app.name === "Order Processor" && (
                <Chip size="sm" color="secondary" variant="shadow">
                  {t("translation:beta")}
                </Chip>
              )}
            </div>
          )}

          <div className="controlButtons flex items-center space-x-4">
            {canImport && !integration?.id && <ImportTemplate appId={app.id} />}
            {integration?.id ? (
              <div />
            ) : toggled ? (
              <Button
                size="sm"
                radius="none"
                variant="bordered"
                color="primary"
                onPress={toggleOff}
              >
                {t("translation:cancel")}
              </Button>
            ) : (
              <Button
                size="sm"
                radius="none"
                variant="solid"
                color="primary"
                onPress={toggleOn}
              >
                {t("translation:new_integration")}
              </Button>
            )}
          </div>
        </div>
      </div>
      {toggled ? children : ""}
      <Outlet />
    </>
  );
}
